import React, {useState} from 'react'
import {motion, AnimatePresence} from 'framer-motion'
import {useFamily} from '../../contexts/FamilyContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import toast from 'react-hot-toast'

const {FiX, FiTrash2, FiAlertTriangle} = FiIcons

const DeleteRewardModal = ({isOpen, onClose, reward}) => {
  const {deleteReward} = useFamily()
  const [loading, setLoading] = useState(false)

  const handleDelete = async () => {
    if (!reward) return

    setLoading(true)

    try {
      const {error} = await deleteReward(reward.id)
      if (!error) {
        onClose()
      }
    } catch (error) {
      console.error('Error deleting reward:', error)
    } finally {
      setLoading(false)
    }
  }

  if (!reward) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{opacity: 0, scale: 0.95, y: 20}}
            animate={{opacity: 1, scale: 1, y: 0}}
            exit={{opacity: 0, scale: 0.95, y: 20}}
            className="relative bg-white rounded-2xl shadow-xl w-full max-w-md"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-xl" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Delete Reward</h2>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="p-2"
                  disabled={loading}
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </Button>
              </div>

              {/* Reward Preview */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border-l-4 border-red-500">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{reward.emoji || '🎁'}</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">{reward.title}</h3>
                    {reward.description && (
                      <p className="text-sm text-gray-600 mt-1">{reward.description}</p>
                    )}
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  ⭐ {reward.points_required} points required
                </div>
              </div>

              {/* Warning Message */}
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-lg mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-red-800 mb-1">
                      Are you sure you want to delete this reward?
                    </h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>• This action cannot be undone</li>
                      <li>• The reward will be permanently removed</li>
                      <li>• Children will no longer see this reward option</li>
                      <li>• Any pending redemption requests will be cancelled</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="ghost"
                  onClick={onClose}
                  className="flex-1"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleDelete}
                  loading={loading}
                  className="flex-1"
                >
                  <SafeIcon icon={FiTrash2} className="text-sm" />
                  {loading ? 'Deleting...' : 'Delete Reward'}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default DeleteRewardModal